/**
 * ИИ-помощник — экран сотрудника (§10 ТЗ сервиса).
 *
 * Серверная часть написана на этапе S6 целиком: стоп-темы, векторный
 * поиск, эскалация, пул пробелов. Показать её было некому — этот экран
 * закрывает разрыв.
 *
 * Три вещи, из-за которых он выглядит именно так:
 *
 * - **текст ответа не собирается здесь.** «Передал вопрос управляющему.
 *   Ответ придёт в течение смены» — нормативная формулировка §10.5, срок
 *   в ней задан решением D57. Второй набор тех же слов в браузере
 *   означал бы, что бот и приложение обещают человеку разное;
 * - **свежий ответ сверху, а не снизу.** Разговор читается сверху вниз,
 *   но у Mini App нет управления прокруткой: при обычном порядке
 *   пятый ответ уезжает за край экрана, и человек его не находит;
 * - **на вопрос про состав и аллергены помощник не отвечает** и уводит в
 *   справочник (D55). Раз уводит — под ответом должна быть кнопка, иначе
 *   совет «посмотрите в справочнике» требует от официанта у стола
 *   вспомнить, где этот справочник (§1 правил понятного интерфейса:
 *   экран обязан отвечать на вопрос «что мне сделать сейчас»).
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, Button, Card } from '@shell/index';
import { studyApi } from '../api';
import type { AssistantReply } from '../api';

/** Один обмен: что спросили и что ответили. История держится в памяти
 *  экрана и живёт до закрытия приложения — сервер её отдавать не умеет и
 *  не должен: личный диалог не показывается никому (D64). */
interface Turn {
  question: string;
  reply: AssistantReply;
}

/**
 * Что сказать вместо ответа, когда запрос не удался.
 *
 * §6 правил понятного интерфейса: ошибка говорит, что не получилось, из-за
 * чего и что делать. Объяснение сервера теперь доходит до браузера
 * (`ApiError.detail`) — например, «модель эмбеддингов не установлена»;
 * своими словами оно не пересказывается.
 */
function failureText(error: unknown, speaker: string): string {
  if (error instanceof ApiError && error.detail) {
    return error.detail;
  }
  if (error instanceof ApiError && error.status >= 500) {
    return (
      `${speaker} сейчас недоступен` +
      ' — это сбой на сервере, а не ваш вопрос. ' +
      'Спросите управляющего напрямую, а о сбое он сообщит разработчику.'
    );
  }
  return (
    'Вопрос не отправился — похоже, пропала связь. ' +
    'Попробуйте ещё раз; если не выйдет, спросите управляющего.'
  );
}

function Sources({ items }: { items: AssistantReply['sources'] }) {
  if (items.length === 0) {
    return null;
  }
  // Источник — обязательная часть ответа (§10.1): без него помощник
  // неотличим от выдумки, и доверия к нему быть не должно.
  return (
    <p className="faint">
      Источник:{' '}
      {items
        .map((item) => (item.anchor ? `${item.title}, ${item.anchor}` : item.title))
        .join(' · ')}
    </p>
  );
}

function Answer({ turn, onOpenCatalog }: { turn: Turn; onOpenCatalog: () => void }) {
  return (
    <Card title={turn.question}>
      <p className="answer">{turn.reply.text}</p>
      <Sources items={turn.reply.sources} />

      {turn.reply.kind === 'catalog' && (
        <Button variant="primary" small onClick={onOpenCatalog}>
          Открыть справочник
        </Button>
      )}
    </Card>
  );
}

/**
 * Необязательные `title` и `speaker` — для раздела «Наставник» портала
 * (D186): там вопрос по регламенту задают наставнику, а не «помощнику».
 * Правила ответа (стоп-темы, эскалация, D64) от подписи не зависят — их
 * держит сервер. В Mini App подписи прежние.
 */
export function Assistant({
  onOpenCatalog,
  title = 'Спросить по регламентам',
  speaker = 'Помощник',
}: {
  onOpenCatalog: () => void;
  title?: string;
  speaker?: string;
}) {
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: (text: string) => studyApi.ask(text),
    onMutate: () => setFailure(null),
    onSuccess: (reply, text) => {
      setTurns((previous) => [{ question: text, reply }, ...previous]);
      setQuestion('');
    },
    onError: (error) => setFailure(failureText(error, speaker)),
  });

  const text = question.trim();
  const submit = () => {
    if (text && !ask.isPending) {
      ask.mutate(text);
    }
  };

  return (
    <>
      <Card title={title}>
        <textarea
          className="search ask"
          rows={3}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            // Enter отправляет, Shift+Enter переносит строку: вопрос из
            // зала — одна фраза, и лишнее нажатие здесь лишнее.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder="Например: за сколько часов гость может отменить бронь"
        />
        {/* §8 правил: кнопки, ведущей в тупик, быть не должно. Пустой
            вопрос сервер отклонит с текстом «Напишите вопрос» — честнее
            не дать нажать. */}
        <Button variant="primary" disabled={!text || ask.isPending} onClick={submit}>
          {ask.isPending ? 'Ищу в документах…' : 'Спросить'}
        </Button>

        {failure && <p className="answer failure">{failure}</p>}
      </Card>

      {turns.length === 0 && !ask.isPending && (
        // Не «пока пусто», а ответ на три вопроса §1 правил: что это,
        // откуда берутся ответы и чего от помощника ждать не надо.
        // Про деньги, увольнения и здоровье он молчит не по недоделке, а
        // по решению D54, и знать это лучше до вопроса, а не после.
        <Card title="Что он знает">
          <p>
            {speaker} отвечает по внутренним документам ресторана — стандартам
            обслуживания, правилам брони, инструкциям — и всегда называет документ,
            из которого взял ответ.
          </p>
          <p className="faint">
            Состав блюда и аллергены он не разбирает: это справочник, там ответ
            точнее. Вопросы о деньгах, графике и происшествиях со здоровьем он не
            решает сам — передаёт управляющему и говорит вам об этом.
          </p>
        </Card>
      )}

      {turns.map((turn, index) => (
        <Answer key={turns.length - index} turn={turn} onOpenCatalog={onOpenCatalog} />
      ))}
    </>
  );
}
